import { getSiteConfig } from '../utils/site';

/**
 * Brand wordmark with an optional parent-brand byline ("By ResponseKit").
 *
 * The byline is rendered smaller and tight beneath the wordmark, right-aligned
 * so its right edge tracks the wordmark's actual right edge (not a wider
 * container). This is achieved by wrapping wordmark + byline in an inline-block
 * lockup that shrinks to the wordmark's text width, with the byline right-
 * aligned inside it.
 *
 * Two visual variants:
 *  - "split" (default): logoPre + accented logoAccent, mono font. Used on the
 *    login/landing header and the loading splash.
 *  - "plain": single-color appName in --accent. Used in the app top navbar.
 *
 * The byline only renders when the active brand defines one (getSiteConfig().
 * byline); ResponseKit's own brand omits it, so it never reads
 * "ResponseKit By ResponseKit".
 */
export function Wordmark({
  variant = 'split',
  size = 26,
  as = 'div',
  className,
  style,
}: {
  variant?: 'split' | 'plain';
  size?: number;
  as?: 'div' | 'span';
  className?: string;
  style?: React.CSSProperties;
}) {
  const b = getSiteConfig();
  const Tag = as;

  // Byline scaled off the wordmark size, floored so it stays legible on the
  // small navbar wordmark. Tight line-height + tiny gap keeps it hugging the
  // wordmark; right alignment tracks the wordmark's right edge via the
  // inline-block lockup.
  const bylineSize = Math.max(10, Math.round(size * 0.46));

  return (
    <Tag
      className={className}
      style={{ display: 'inline-block', textAlign: 'left', ...style }}
    >
      <span
        style={{
          display: 'block',
          fontFamily: variant === 'split' ? b.fontMono : undefined,
          fontWeight: variant === 'split' ? 600 : 700,
          fontSize: size,
          letterSpacing: '-0.5px',
          lineHeight: 1.05,
          color: variant === 'plain' ? 'var(--accent)' : undefined,
        }}
      >
        {variant === 'split' ? (
          <>
            {b.logoPre}
            <span style={{ color: b.accent }}>{b.logoAccent}</span>
          </>
        ) : (
          b.appName
        )}
      </span>
      {b.byline && (
        <span
          style={{
            display: 'block',
            fontFamily: variant === 'split' ? b.fontMono : b.fontSans,
            fontSize: bylineSize,
            fontWeight: 400,
            letterSpacing: '-0.2px',
            color: b.textMuted,
            textAlign: 'right',
            lineHeight: 1,
            marginTop: 1,
          }}
        >
          {b.byline}
        </span>
      )}
    </Tag>
  );
}
